import * as React from 'react';
import { Loader2, X, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';
import { EntityActionList, EntityActionKey, EntityActionHandlers } from './entityActions';
import { Kbd, useZoningHotkey } from './hotkeys';

interface EditToolbarProps extends EntityActionHandlers {
  activeType: string | null;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
  /** Called for entity-level actions (edit details, delete). */
  onAction: (key: EntityActionKey) => void;
}

/** Page-level toolbar shown while a polygon is being edited on the canvas.
 *  Lives in the same slot as the polygon-creation toolbar, since the two
 *  states are mutually exclusive. */
export function EditToolbar({ activeType, saving, onCancel, onSave, onAction, isAvailable }: EditToolbarProps) {
  const { t } = useT();
  const typeLabel = activeType ? t(`zoning.types.${activeType}`) : '';
  // Esc (cancel) and Del (delete) live in CanvasRenderer next to the point
  // shortcuts; E and S belong to this toolbar's buttons. «у»/«ы» — the same
  // physical keys in the Russian layout.
  useZoningHotkey(['e', 'E', 'у', 'У'], () => { if (isAvailable?.('edit') ?? true) onAction('edit'); }, !saving);
  useZoningHotkey(['s', 'S', 'ы', 'Ы'], onSave, !saving);

  return (
    <div className="flex w-full flex-nowrap items-center gap-3">
      <span className="text-sm text-muted-foreground">
        <span className="font-medium text-foreground">{t('drawing.editing')}</span>
        {typeLabel ? <span>: {typeLabel}</span> : null}
      </span>

      <div className="ml-auto flex flex-nowrap items-center gap-1.5">
        <Button type="button" size="sm" variant="outline" disabled={saving} onClick={onCancel}>
          <X className="size-3.5" />
          <span>{t('drawing.cancel')}</span>
          <Kbd>Esc</Kbd>
        </Button>

        <EntityActionList
          layout="toolbar"
          t={t}
          onAction={onAction}
          isAvailable={isAvailable}
          disabled={saving}
        />

        <Button type="button" size="sm" variant="default" disabled={saving} onClick={onSave}>
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />}
          <span>{t('drawing.save')}</span>
          <Kbd>S</Kbd>
        </Button>
      </div>
    </div>
  );
}
